import axios from 'axios';
import setAuthToken from '../utils/setAuthToken';
import {
    GET_DASHBOARD_STATS,
    GET_DASHBOARD_DETAILED_STATS,
    DASHBOARD_LOADING,
    DASHBOARD_ERROR,
    GET_USER_ACTIVITY_REPORT,
    GET_USER_ANALYTICS_REPORT,
    GET_USER_SUMMARY_REPORT,
    USER_ACTIVITY_REPORT_LOADING,
    USER_ACTIVITY_REPORT_ERROR
} from './types';

const API_URL = process.env.REACT_APP_SERVER_API_URL;

// Get dashboard statistics
export const getDashboardStats = () => async dispatch => {
    try {
        dispatch({ type: DASHBOARD_LOADING });
        const res = await axios.get(`${API_URL}/dashboard`);
        dispatch({
            type: GET_DASHBOARD_STATS,
            payload: res.data.data
        });
    } catch (err) {
        dispatch({
            type: DASHBOARD_ERROR,
            payload: err.response?.data?.msg || err.message
        });
    }
};

// Get detailed dashboard statistics (with revenue)
export const getDashboardDetailedStats = () => async dispatch => {
    try {
        dispatch({ type: DASHBOARD_LOADING });
        const res = await axios.get(`${API_URL}/dashboard/detailed`);
        dispatch({
            type: GET_DASHBOARD_DETAILED_STATS,
            payload: res.data.data
        });
    } catch (err) {
        dispatch({
            type: DASHBOARD_ERROR,
            payload: err.response?.data?.msg || err.message
        });
    }
};

// Get both statistics at once
export const getDashboardData = () => async dispatch => {
    try {
        dispatch({ type: DASHBOARD_LOADING });
        
        const [statsResponse, detailedResponse] = await Promise.all([
            axios.get(`${API_URL}/dashboard`),
            axios.get(`${API_URL}/dashboard/detailed`)
        ]);
        
        dispatch({
            type: GET_DASHBOARD_STATS,
            payload: statsResponse.data.data
        });
        
        dispatch({
            type: GET_DASHBOARD_DETAILED_STATS,
            payload: detailedResponse.data.data
        });
    } catch (err) {
        dispatch({
            type: DASHBOARD_ERROR,
            payload: err.response?.data?.msg || err.message
        });
    }
};

// Get User Activity Report
export const getUserActivityReport = (filters = {}, exportExcel = false) => async dispatch => {
    if (localStorage.token) {
        setAuthToken(localStorage.token);
    }
    try {
        dispatch({ type: USER_ACTIVITY_REPORT_LOADING }); 
        let url = `${API_URL}/dashboard/user-activity-report`;
        const params = new URLSearchParams();
        if (filters.userId) {
            params.append('userId', filters.userId);
        }
        if (filters.registeredFrom) {
            params.append('registeredFrom', filters.registeredFrom);
        }
        if (filters.registeredTo) {
            params.append('registeredTo', filters.registeredTo);
        }
        if (filters.lastOrderFrom) {
            params.append('lastOrderFrom', filters.lastOrderFrom);
        }
        if (filters.lastOrderTo) {
            params.append('lastOrderTo', filters.lastOrderTo);
        }
        if (filters.lastDeliveryFrom) {
            params.append('lastDeliveryFrom', filters.lastDeliveryFrom);
        }
        if (filters.lastDeliveryTo) {
            params.append('lastDeliveryTo', filters.lastDeliveryTo);
        }
        if (exportExcel) {
            params.append('export', 'excel');
        }
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        if (exportExcel) {
            // For Excel export, download the file
            const response = await axios.get(url, {
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: 'text/csv' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `user-activity-report-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            dispatch({ type: USER_ACTIVITY_REPORT_LOADING, payload: false });
        } else {
            const res = await axios.get(url);
            dispatch({
                type: GET_USER_ACTIVITY_REPORT,
                payload: res.data
            });
        }
    } catch (err) {
        dispatch({
            type: USER_ACTIVITY_REPORT_ERROR,
            payload: err.response?.data?.msg || err.message
        });
    }
};

// Get User Analytics Report
export const getUserAnalyticsReport = (exportExcel = false) => async dispatch => {
    if (localStorage.token) {
        setAuthToken(localStorage.token);
    }
    try {
        dispatch({ type: USER_ACTIVITY_REPORT_LOADING });
        let url = `${API_URL}/dashboard/user-analytics-report`;
        if (exportExcel) {
            url += '?export=excel';
        }
        if (exportExcel) {
            // For Excel export, download the file
            const response = await axios.get(url, {
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: 'text/csv' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `user-analytics-report-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            dispatch({ type: USER_ACTIVITY_REPORT_LOADING, payload: false });
        } else {
            const res = await axios.get(url);
            dispatch({
                type: GET_USER_ANALYTICS_REPORT,
                payload: res.data
            });
        }
    } catch (err) {
        dispatch({
            type: USER_ACTIVITY_REPORT_ERROR,
            payload: err.response?.data?.msg || err.message
        });
    }
};

// Get User Summary Report
export const getUserSummaryReport = (filters = {}, exportExcel = false) => async dispatch => {
    if (localStorage.token) {
        setAuthToken(localStorage.token);
    }
    try {
        dispatch({ type: USER_ACTIVITY_REPORT_LOADING });
        let url = `${API_URL}/dashboard/user-summary-report`;
        const params = new URLSearchParams();
        if (filters.registeredFrom) {
            params.append('registeredFrom', filters.registeredFrom);
        }
        if (filters.registeredTo) {
            params.append('registeredTo', filters.registeredTo);
        }
        if (filters.firstPurchaseFrom) {
            params.append('firstPurchaseFrom', filters.firstPurchaseFrom);
        }
        if (filters.firstPurchaseTo) {
            params.append('firstPurchaseTo', filters.firstPurchaseTo);
        }
        if (exportExcel) {
            params.append('export', 'excel');
        }
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        if (exportExcel) {
            // For Excel export, download the file
            const response = await axios.get(url, {
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: 'text/csv' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `user-summary-report-${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            dispatch({ type: USER_ACTIVITY_REPORT_LOADING, payload: false });
        } else {
            const res = await axios.get(url);
            dispatch({
                type: GET_USER_SUMMARY_REPORT,
                payload: res.data
            });
        }
    } catch (err) {
        dispatch({
            type: USER_ACTIVITY_REPORT_ERROR,
            payload: err.response?.data?.msg || err.message
        });
    }
};

